import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, MessageSquare, Bot, Archive, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ChatContainer } from '@/components/ai/chat-container'
import { AgentSelector } from '@/components/ai/agent-selector'
import {
  useRecentConversations,
  useConversation,
  useCreateConversation,
  useActiveAgents,
  useDeleteConversation,
  useArchiveConversation,
} from '@/lib/hooks'
import type { Conversation, Agent, ConversationStatus } from '@/lib/api/types'

/**
 * AI Chat Page
 *
 * Main chat interface with:
 * - Conversation list sidebar
 * - Agent selection for new conversations
 * - ChatContainer for messaging
 */
export default function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [selectedAgentSlug, setSelectedAgentSlug] = useState<string>('')
  const [isCreatingConversation, setIsCreatingConversation] = useState(false)

  // Get conversation ID from URL
  const conversationId = searchParams.get('id') || ''
  const isNewChat = !conversationId

  // Fetch conversations and agents
  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
    refetch: refetchConversations,
  } = useRecentConversations(undefined, 50)
  const { data: agents = [] } = useActiveAgents()
  const { data: currentConversation } = useConversation(conversationId, {
    queryOptions: { enabled: !!conversationId },
  })

  // Mutations
  const createConversation = useCreateConversation()
  const deleteConversation = useDeleteConversation()
  const archiveConversation = useArchiveConversation()

  // Find the agent for the current conversation
  const currentAgent = useMemo(() => {
    if (isNewChat) {
      return agents.find((a) => a.slug === selectedAgentSlug)
    }
    if (currentConversation?.agentId) {
      return agents.find((a) => a.id === currentConversation.agentId)
    }
    return undefined
  }, [isNewChat, selectedAgentSlug, currentConversation, agents])

  // Set default agent when agents load and we're in new chat mode
  useEffect(() => {
    const firstAgent = agents[0]
    if (isNewChat && firstAgent && !selectedAgentSlug) {
      setSelectedAgentSlug(firstAgent.slug)
    }
  }, [isNewChat, agents, selectedAgentSlug])

  // Handle selecting a conversation
  const handleSelectConversation = useCallback(
    (id: string) => {
      setSearchParams({ id })
    },
    [setSearchParams]
  )

  // Handle starting a new chat
  const handleNewChat = useCallback(() => {
    setSearchParams({})
    const firstAgent = agents.length > 0 ? agents[0] : undefined
    setSelectedAgentSlug(firstAgent?.slug ?? '')
  }, [setSearchParams, agents])

  // Handle creating a conversation when first message is sent
  const handleCreateConversation = useCallback(
    async (agentSlug: string): Promise<string | null> => {
      const agent = agents.find((a) => a.slug === agentSlug)
      if (!agent) return null

      setIsCreatingConversation(true)
      try {
        const conversation = await createConversation.mutateAsync({
          agentId: agent.id,
        })
        // Update URL with new conversation ID
        setSearchParams({ id: conversation.id })
        await refetchConversations()
        return conversation.id
      } catch (error) {
        console.error('Failed to create conversation:', error)
        return null
      } finally {
        setIsCreatingConversation(false)
      }
    },
    [agents, createConversation, setSearchParams, refetchConversations]
  )

  // Handle archiving a conversation
  const handleArchive = useCallback(
    async (id: string) => {
      try {
        await archiveConversation.mutateAsync(id)
        if (conversationId === id) {
          setSearchParams({})
        }
        await refetchConversations()
      } catch (error) {
        console.error('Failed to archive conversation:', error)
      }
    },
    [archiveConversation, conversationId, setSearchParams, refetchConversations]
  )

  // Handle deleting a conversation
  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteConversation.mutateAsync(id)
        if (conversationId === id) {
          setSearchParams({})
        }
        await refetchConversations()
      } catch (error) {
        console.error('Failed to delete conversation:', error)
      }
    },
    [deleteConversation, conversationId, setSearchParams, refetchConversations]
  )

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'flex flex-col border-r border-gray-200 bg-gray-50 transition-all duration-200 dark:border-gray-700 dark:bg-gray-900',
          sidebarCollapsed ? 'w-16' : 'w-80'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-14 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-700">
          {!sidebarCollapsed && (
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Conversations
            </h2>
          )}
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleNewChat}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">New Chat</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Conversation list */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {isLoadingConversations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                {sidebarCollapsed ? '' : 'No conversations yet'}
              </div>
            ) : (
              conversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  agents={agents}
                  isSelected={conversation.id === conversationId}
                  isCollapsed={sidebarCollapsed}
                  onSelect={handleSelectConversation}
                  onArchive={handleArchive}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </aside>

      {/* Main chat area */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Header with agent selector for new chats */}
        {isNewChat && (
          <div className="flex items-center gap-4 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Chat with:
            </span>
            <AgentSelector
              value={selectedAgentSlug}
              onChange={setSelectedAgentSlug}
              disabled={isCreatingConversation}
            />
            {isCreatingConversation && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
        )}

        {/* Chat header for existing conversations */}
        {!isNewChat && currentAgent && (
          <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
              <Bot className="h-4 w-4 text-primary-700 dark:text-primary-300" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {currentConversation?.title || currentAgent.name}
              </h1>
              <p className="text-xs text-gray-500">{currentAgent.description}</p>
            </div>
          </div>
        )}

        {/* Chat container or empty state */}
        {isNewChat ? (
          <NewChatState
            agent={currentAgent}
            agentSlug={selectedAgentSlug}
            onCreateConversation={handleCreateConversation}
            isCreating={isCreatingConversation}
          />
        ) : (
          <ChatContainer
            conversationId={conversationId}
            agent={currentAgent}
            className="flex-1"
            onMessageSent={() => refetchConversations()}
          />
        )}
      </main>
    </div>
  )
}

/**
 * Conversation item in the sidebar
 */
interface ConversationItemProps {
  conversation: Conversation
  agents: Agent[]
  isSelected: boolean
  isCollapsed: boolean
  onSelect: (id: string) => void
  onArchive: (id: string) => void
  onDelete: (id: string) => void
}

function ConversationItem({
  conversation,
  agents,
  isSelected,
  isCollapsed,
  onSelect,
  onArchive,
}: ConversationItemProps) {
  const agent = agents.find((a) => a.id === conversation.agentId)
  const title = conversation.title || agent?.name || 'Untitled'
  const isArchived: boolean = conversation.status === ('Archived' as ConversationStatus)

  const formattedDate = useMemo(() => {
    const date = new Date(conversation.updatedAt || conversation.createdAt)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }, [conversation.updatedAt, conversation.createdAt])

  if (isCollapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onSelect(conversation.id)}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                isSelected
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">{title}</p>
            <p className="text-xs text-gray-400">{formattedDate}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div
      className={cn(
        'group relative mb-1 rounded-lg p-3 transition-colors',
        isSelected
          ? 'bg-primary-100 dark:bg-primary-900/50'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800',
        isArchived && 'opacity-60'
      )}
    >
      <button
        onClick={() => onSelect(conversation.id)}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
            <MessageSquare className="h-4 w-4 text-gray-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {title}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {agent && <span className="truncate">{agent.name}</span>}
              <span>·</span>
              <span className="shrink-0">{formattedDate}</span>
            </div>
          </div>
        </div>
      </button>

      {/* Actions on hover */}
      {!isArchived && (
        <div className="absolute right-2 top-2 hidden group-hover:flex">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                    onArchive(conversation.id)
                  }}
                >
                  <Archive className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  )
}

/**
 * Empty state for new chats
 */
interface NewChatStateProps {
  agent?: Agent
  agentSlug: string
  onCreateConversation: (agentSlug: string) => Promise<string | null>
  isCreating: boolean
}

function NewChatState({
  agent,
  agentSlug,
  onCreateConversation,
  isCreating,
}: NewChatStateProps) {
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)

  const handleSend = async (content: string) => {
    if (!content.trim() || !agentSlug || isSending || isCreating) return

    setIsSending(true)
    const conversationId = await onCreateConversation(agentSlug)

    if (conversationId) {
      // The conversation was created and URL updated
      // ChatContainer will now be rendered and we need to trigger the message
      // For now, we store it - the ChatContainer will pick up from URL change
      // This is a bit of a race condition, but the URL update triggers re-render
      setIsSending(false)
    } else {
      setIsSending(false)
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Empty state message */}
      <div className="flex flex-1 flex-col items-center justify-center p-8">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
          <MessageSquare className="h-10 w-10 text-gray-400" />
        </div>
        <h2 className="mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          Start a new conversation
        </h2>
        <p className="mt-2 max-w-md text-center text-sm text-gray-500">
          {agent
            ? `You're about to chat with ${agent.name}. ${agent.description || 'Send a message to get started.'}`
            : 'Select an agent above and send your first message to begin.'}
        </p>

        {/* Quick prompts */}
        {agent && (
          <div className="mt-8 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
            {getQuickPrompts(agent).map((prompt, index) => (
              <button
                key={index}
                onClick={() => setInputValue(prompt)}
                className="rounded-lg border border-gray-200 bg-white p-4 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 p-4 dark:border-gray-700">
        <div className="mx-auto max-w-3xl">
          <div className="flex gap-3">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend(inputValue)
                }
              }}
              placeholder={agent ? `Message ${agent.name}...` : 'Select an agent to start...'}
              disabled={!agentSlug || isSending || isCreating}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            <Button
              onClick={() => handleSend(inputValue)}
              disabled={!inputValue.trim() || !agentSlug || isSending || isCreating}
            >
              {isSending || isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Send'
              )}
            </Button>
          </div>
          <p className="mt-2 text-center text-xs text-gray-400">
            AI may produce inaccurate information. Verify important details.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Get quick prompts based on agent
 */
function getQuickPrompts(_agent: Agent): string[] {
  // Default prompts - could be customized per agent in the future
  const defaultPrompts = [
    'Help me get started',
    'What can you help me with?',
    'Give me some examples',
    'Explain your capabilities',
  ]

  // Agent-specific prompts could be added here based on _agent.slug or _agent.tags
  return defaultPrompts
}
